/**
 * SHA-256 of the legal text that was actually on the page at signing time.
 *
 * WHY THIS EXISTS
 * A version stamp ("2026-09-01") says *which* document someone agreed to only as
 * long as you trust the party holding the git history — which is us, the party
 * with the incentive. A hash taken in the signer's own browser, from the same DOM
 * the expand panel renders, lets anyone recompute it later from a copy of the text
 * and see for themselves whether it is the document that was signed.
 *
 * That is the difference between "trust our repo" and "here is the fingerprint,
 * check it yourself", and it is the part of the E-SIGN Act retention requirement
 * that a version string alone does not satisfy.
 *
 * ⚠️ NORMALISATION IS PART OF THE CONTRACT.
 * The hash is computed over `textContent` with runs of whitespace collapsed to a
 * single space and the ends trimmed. Markup, indentation and line wrapping are
 * therefore NOT part of the hash — restyling the page does not invalidate past
 * signatures, but changing a word does. If you ever change this normalisation you
 * silently break every hash recorded before the change, so don't: add a v2
 * alongside it instead.
 */

/** Collapse whitespace so formatting changes don't invalidate historical hashes. */
export function normalizeLegalText(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

/** Hex SHA-256. Returns null where WebCrypto is unavailable — never throws. */
export async function sha256Hex(text: string): Promise<string | null> {
  try {
    // crypto.subtle requires a secure context. On plain http:// (a LAN dev host,
    // say) it is undefined, and a signup must not fail over a missing audit field.
    if (typeof window === 'undefined' || !window.crypto?.subtle) return null;
    const bytes = new TextEncoder().encode(text);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/** Hash the rendered text of an element, or null if it isn't mounted. */
export async function hashElementText(el: HTMLElement | null): Promise<string | null> {
  if (!el) return null;
  // textContent, NOT innerText: innerText is layout-aware and returns '' for the
  // hidden container we hash from. textContent reads the same characters whether
  // the panel is expanded or collapsed, which is the point.
  const text = normalizeLegalText(el.textContent || '');
  if (text.length < 100) return null; // nothing meaningful rendered
  return sha256Hex(text);
}
